import { useCallback, useRef } from "react";
import { getSelectedPropertyExpediaHotelIds } from "../pages/price-check/utils";
import {
  DEFAULT_FILTER_VALUES,
  persistFilterValues,
} from "../components/priceCheck-form/PriceCheckFilterUtils";
import {
  convertFilterOrderToAPI,
  processFiltersForAPI,
} from "../utils/filterUtils";

export const usePriceCheckTabChange = ({
  formValues,
  handleSearch,
  priceCheckPage,
  properties,
  setSelectedFilters,
  setFilterSelectionOrder,
  setPriceCheckPage,
  selectedFilters,
  filterSelectionOrder,
  activeTab,
  setExpandedRowKeys,
}) => {
  // Store filter states for each tab
  const tabFilterStates = useRef(new Map());

  // Helper function to clear all tab filter states
  const clearAllTabFilterStates = useCallback(() => {
    tabFilterStates.current.clear();
  }, []);

  // Helper function to store current tab's filter state
  const storeCurrentTabFilters = useCallback(
    (previousTabKey) => {
      if (previousTabKey && (selectedFilters || filterSelectionOrder)) {
        const apiFilterSelectionOrder = convertFilterOrderToAPI(
          filterSelectionOrder || []
        );

        const filterStateToStore = {
          selectedFilters: { ...selectedFilters },
          filterSelectionOrder: [...(filterSelectionOrder || [])],
          filterStack: apiFilterSelectionOrder,
        };
        tabFilterStates.current.set(previousTabKey, filterStateToStore);
      }
    },
    [selectedFilters, filterSelectionOrder]
  );

  // Helper function to get stored or default filter state
  const getFilterStateForTab = useCallback((newTabKey) => {
    const storedFilterState = tabFilterStates.current.get(newTabKey);
    return (
      storedFilterState || {
        selectedFilters: DEFAULT_FILTER_VALUES.selectedFilters,
        filterSelectionOrder: DEFAULT_FILTER_VALUES.filterSelectionOrder,
        filterStack: [],
      }
    );
  }, []);

  // Helper function to determine hotel IDs for tab
  const getHotelIdsForTab = useCallback(
    (tabKey, specificHotelId) => {
      if (tabKey === "all" || !specificHotelId) {
        return getSelectedPropertyExpediaHotelIds(
          properties,
          formValues?.selectedCity || [],
          formValues?.selectedProperties || []
        );
      }
      return [specificHotelId];
    },
    [properties, formValues?.selectedCity, formValues?.selectedProperties]
  );

  const handleTabChange = useCallback(
    (tabKey, specificHotelId) => {
      // Only trigger API call if we have form values
      if (!formValues || Object.keys(formValues).length === 0) {
        return;
      }

      // Generate consistent tab keys for storing and retrieving filters
      // Use the current activeTab for storing (the tab we're leaving)
      const currentTabKey = activeTab === "all" ? "all" : activeTab;
      // Use the tabKey (propertyId) for retrieving (the tab we're switching to)
      const newTabKey = tabKey === "all" ? "all" : tabKey;

      // Store current filter state for the tab we're leaving
      storeCurrentTabFilters(currentTabKey);

      // Get stored filter state for the tab we're switching to, or use defaults
      const filtersToApply = getFilterStateForTab(newTabKey);

      // Ensure filtersToApply has the required structure
      if (!filtersToApply?.selectedFilters) {
        return; // Exit early if filters are not properly structured
      }

      // Update filter state to stored values or defaults
      setSelectedFilters(filtersToApply.selectedFilters);
      setFilterSelectionOrder(filtersToApply.filterSelectionOrder);

      // Persist the filter values
      persistFilterValues(
        filtersToApply.selectedFilters,
        filtersToApply.filterSelectionOrder
      );

      // Reset pagination to page 1 in UI state
      setPriceCheckPage((prevState) => ({
        ...prevState,
        number: 0,
      }));

      // Close all expanded rows when switching tabs
      setExpandedRowKeys([]);

      // Determine hotel IDs and convert filters
      const hotelIds = getHotelIdsForTab(tabKey, specificHotelId);
      const apiFilters = processFiltersForAPI(filtersToApply.selectedFilters);

      // Call search with the same form values but specific hotel IDs and stored/restored filters
      handleSearch(
        formValues,
        {
          page: 1,
          pageSize: priceCheckPage.size,
        },
        apiFilters,
        filtersToApply.filterStack,
        false,
        hotelIds
      );
    },
    [
      formValues,
      handleSearch,
      priceCheckPage.size,
      setSelectedFilters,
      setFilterSelectionOrder,
      setPriceCheckPage,
      setExpandedRowKeys,
      activeTab,
      storeCurrentTabFilters,
      getFilterStateForTab,
      getHotelIdsForTab,
    ]
  );

  return {
    handleTabChange,
    clearAllTabFilterStates,
    getFilterStateForTab,
    storeCurrentTabFilters,
  };
};
